import React from 'react';
import DOMPurify from 'dompurify';
import * as Dialog from '@radix-ui/react-dialog';
import {
  Copy,
  FileCode,
  Download,
  ChevronDown,
  X,
  FileText,
  Loader2,
  FileText as FileTextIcon,
  FileDown,
  Pencil,
  AlertTriangle,
  RotateCcw,
} from 'lucide-react';
import { Interpretation } from '../types';
import { renderMarkdownToHtml, buildMarkdownFromSections } from '../utils/helpers';
import { InterpretationSections } from './InterpretationSections';

/**
 * Pełnoekranowy widok wyniku diagnozy (Radix Dialog: focus trap, Esc, portal,
 * scroll-lock). Działa też podczas streamingu — pokazuje wtedy przewijający
 * się podgląd, jak w panelu.
 *
 * Edycja diagnozy JAKO CAŁOŚCI (PLAN-REFACTOR 4.2, bez per-sekcja):
 * przycisk „Edytuj" otwiera CAŁĄ odpowiedź markdown w jednym edytorze
 * (źródło: savedRawText, a gdy brak — buildMarkdownFromSections).
 * Pasek narzędzi: przełącznik Edycja/Podgląd, licznik znaków, Zapisz/Anuluj.
 * Esc w edycji = Anuluj (confirm przy zmianach), NIE zamyka dialogu;
 * zamknięcie dialogu z niezapisanymi zmianami też wymaga confirm.
 *
 * Rozszerzanie = FLIP (plan 4.3 A): overlay WYRASTA z prostokąta panelu wyniku
 * (origin rect) do pełnego ekranu i zwija się z powrotem przy zamknięciu —
 * Web Animations API (element.animate()), forceMount + własny stan `rendered`
 * opóźniający odmontowanie. Brak originRect / prefers-reduced-motion → fade.
 * Tło overlayu: .fullscreen-result-overlay — solid rgba BEZ backdrop-filter,
 * celowo: blur na pełnym ekranie zabija wydajność; patrz index.css.
 */
interface FullscreenResultDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Prostokąt panelu wyniku zmierzony tuż przed otwarciem — origin animacji
   * FLIP (overlay WYRASTA z tego miejsca do pełnego ekranu i zwija się z
   * powrotem przy zamknięciu). null = brak origin (np. auto-otwarcie na
   * starcie generacji) → zwykły fade. prefers-reduced-motion też = fade.
   */
  originRect?: DOMRect | null;
  patientName: string;
  interpretation: Interpretation | null;
  isStreaming: boolean;
  streamingText: string;
  /** Pełny markdown odpowiedzi (savedRawText || rawStreamText). */
  rawText: string;
  onCopyAll: () => void;
  onCopyMarkdown: () => void;
  onDownloadReport: (format: 'txt' | 'docx' | 'markdown' | 'pdf') => void;
  onCopySection: (title: string, content: string) => void;
  /**
   * Zapis edytowanego markdownu całości diagnozy — rodzic re-parsuje go do
   * sekcji (aiService.parseResponsePublic) i aktualizuje savedRawText.
   * Brak callbacku = edycja niedostępna.
   */
  onSaveEditedMarkdown?: (markdown: string) => void;
  /** Diagnoza była ręcznie redagowana — dyskretny badge „edytowano". */
  diagnosisEdited?: boolean;
  /**
   * Status ostatniej generacji (lastGenerationStatus z useAIIntegration).
   * 'error' przy otwartym arkuszu = W6: pasek błędu nad częściową treścią
   * (treść częściowa zostaje — salvage w hooku).
   */
  generationStatus?: 'success' | 'error' | null;
  /** Ponów ostatnią generację (retryGeneration) — przycisk w pasku błędu W6. */
  onRetry?: () => void;
}

const EXPORT_OPTIONS: Array<{
  format: 'txt' | 'docx' | 'markdown' | 'pdf';
  label: string;
  icon: React.ReactNode;
}> = [
  { format: 'txt', label: 'Plik TXT (Zwykły tekst)', icon: <FileTextIcon className="w-4 h-4 text-txt-tertiary" /> },
  { format: 'docx', label: 'Plik DOCX (Microsoft Word)', icon: <FileDown className="w-4 h-4 text-blue-500" /> },
  { format: 'markdown', label: 'Plik MD (Markdown)', icon: <FileText className="w-4 h-4 text-accent" /> },
  { format: 'pdf', label: 'Plik PDF (Format wektorowy)', icon: <FileText className="w-4 h-4 text-red-500" /> },
];

const UNSAVED_CHANGES_CONFIRM = 'Masz niezapisane zmiany w diagnozie — odrzucić je?';

export const FullscreenResultDialog: React.FC<FullscreenResultDialogProps> = ({
  open,
  onOpenChange,
  originRect = null,
  patientName,
  interpretation,
  isStreaming,
  streamingText,
  rawText,
  onCopyAll,
  onCopyMarkdown,
  onDownloadReport,
  onCopySection,
  onSaveEditedMarkdown,
  diagnosisEdited = false,
  generationStatus = null,
  onRetry,
}) => {
  const [exportMenuOpen, setExportMenuOpen] = React.useState(false);
  const streamScrollRef = React.useRef<HTMLDivElement>(null);

  // ---------------------------------------------------------------------------
  // Rozszerzanie FLIP (plan 4.3 A) — overlay wyrasta z prostokąta panelu wyniku.
  //
  // Implementacja: Web Animations API (element.animate()). Dialog jest
  // forceMount-owany, własny stan `rendered` opóźnia odmontowanie do końca
  // animacji zwijania. Przy otwarciu animujemy content OD transform
  // (translate+scale z origin rect) DO tożsamości; overlay fade in. Przy
  // zamknięciu — odwrotnie (zwija się do tego samego prostokąta).
  // prefers-reduced-motion albo brak originRect → zwykły fade (klasa CSS).
  // ---------------------------------------------------------------------------
  const overlayRef = React.useRef<HTMLDivElement>(null);
  // contentNode trzymany w stanie-refie: callback ref odpala FLIP-in DOKŁADNIE
  // w chwili, gdy Radix zamontuje portalowy content (timing layout-effectu vs
  // portal okazał się zawodny — content.ref bywał null).
  const contentRef = React.useRef<HTMLDivElement | null>(null);
  // Czy dialog jest fizycznie w DOM (forceMount). Pozostaje true podczas
  // animacji zwijania, dopiero potem false.
  const [rendered, setRendered] = React.useState(open);
  // Aktualne `open` w refie — callbacki animacji zwijania (onfinish/oncancel)
  // to closure'y z chwili startu animacji; przy błyskawicznym zamknij→otwórz
  // miałyby stare `open` i odmontowałyby świeżo otwarty dialog (race C-1).
  const openRef = React.useRef(open);
  React.useEffect(() => {
    openRef.current = open;
  }, [open]);
  // Origin „zamrożony" w chwili otwarcia — zamknięcie zwija do tego samego
  // prostokąta nawet jeśli panel pod spodem się przesunął.
  const originRef = React.useRef<DOMRect | null>(null);
  const flipAnimRef = React.useRef<Animation | null>(null);
  // FLIP-in DOKŁADNIE RAZ na otwarcie (anty-migotanie, DoD 5.3): Radix składa
  // przekazany callback ref w composeRefs tworzony NA KAŻDY render, więc React
  // odpina i przypina nasz ref przy każdym re-renderze dialogu (np. przełączenie
  // Edycja/Podgląd) — bez tej blokady każdy re-render replay'ował animację
  // FLIP-in (element.animate nie widać w animationstart, stąd podstępne).
  // Reset na krawędzi zamknięcia (open true→false), żeby reopen znów animował.
  const flipInDoneRef = React.useRef(false);

  const prefersReducedMotion = React.useCallback(
    () =>
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches,
    []
  );

  // Zbuduj transform przenoszący content arkusza do prostokąta origin
  // (skala + translacja środków). final rect = RZECZYWISTY getBoundingClientRect
  // contentu po layoutcie (plan 5.1) — content nie jest już pełnym viewportem,
  // tylko wycentrowanym arkuszem ≤56rem, więc liczenie z window.innerWidth/
  // Height dawałoby złe przesunięcie/skalę. Zwraca '' gdy brak sensownych rectów.
  const buildFlipTransform = React.useCallback(
    (originRect: DOMRect | null, finalRect: DOMRect | null): string => {
      if (!originRect || originRect.width < 1 || originRect.height < 1) return '';
      if (!finalRect || finalRect.width < 1 || finalRect.height < 1) return '';
      const scaleX = originRect.width / finalRect.width;
      const scaleY = originRect.height / finalRect.height;
      const translateX =
        originRect.left + originRect.width / 2 - (finalRect.left + finalRect.width / 2);
      const translateY =
        originRect.top + originRect.height / 2 - (finalRect.top + finalRect.height / 2);
      return `translate(${translateX}px, ${translateY}px) scale(${scaleX}, ${scaleY})`;
    },
    []
  );

  const FLIP_TIMING: KeyframeAnimationOptions = {
    duration: 320,
    easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
    fill: 'both',
  };

  // Animacja wejścia (FLIP-in albo fade) — uruchamiana z callback refu, gdy
  // content faktycznie istnieje w DOM.
  const runFlipIn = React.useCallback((content: HTMLDivElement) => {
    flipAnimRef.current?.cancel();
    overlayRef.current?.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 220, easing: 'ease-out', fill: 'both' });

    // Final rect mierzony PO cancel() poprzedniej animacji (fill:both mogłaby
    // zostawić transform) — w tym punkcie content stoi w docelowym layoucie.
    const fromTransform = prefersReducedMotion()
      ? ''
      : buildFlipTransform(originRef.current, content.getBoundingClientRect());
    if (!fromTransform) {
      // Fade (brak originRect / reduced-motion): NIE promujemy warstwy GPU
      // przez will-change — niepotrzebna warstwa kompozytora grzeje bez zysku.
      content.style.transform = '';
      flipAnimRef.current = content.animate(
        [{ opacity: 0 }, { opacity: 1 }],
        { duration: 220, easing: 'ease-out', fill: 'both' }
      );
      return;
    }
    content.style.transformOrigin = 'center center';
    // will-change: transform TYLKO na czas animacji FLIP — zdejmowane w onfinish,
    // by nie trzymać warstwy GPU bez potrzeby (trzymana stale = grzanie Maca).
    content.style.willChange = 'transform';
    const anim = content.animate(
      [
        { transform: fromTransform, opacity: 0.6 },
        { transform: 'translate(0px, 0px) scale(1, 1)', opacity: 1 },
      ],
      FLIP_TIMING
    );
    const clearWillChange = () => {
      content.style.willChange = '';
    };
    anim.onfinish = clearWillChange;
    anim.oncancel = clearWillChange;
    flipAnimRef.current = anim;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefersReducedMotion, buildFlipTransform]);

  // Callback ref na content: w chwili podpięcia węzła (mount) odpalamy FLIP-in.
  const setContentNode = React.useCallback((node: HTMLDivElement | null) => {
    contentRef.current = node;
    if (node && open && !flipInDoneRef.current) {
      // Raz na otwarcie (flipInDoneRef) — Radix re-przypina ref co render.
      flipInDoneRef.current = true;
      // Po layoutcie (rAF), żeby getBoundingClientRect/origin były aktualne.
      requestAnimationFrame(() => {
        if (contentRef.current === node) runFlipIn(node);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, runFlipIn]);

  // OTWARCIE: zamrażamy origin i montujemy (rendered=true → portal renderuje
  // content → callback ref setContentNode odpala FLIP-in).
  React.useEffect(() => {
    if (open) {
      originRef.current = originRect;
      setRendered(true);
    }
  }, [open, originRect]);

  // ZAMKNIĘCIE: gdy open przechodzi true→false, odpal FLIP-out, potem odmontuj.
  const prevOpenRef = React.useRef(open);
  React.useEffect(() => {
    const wasOpen = prevOpenRef.current;
    prevOpenRef.current = open;
    if (wasOpen && !open && rendered) {
      // Reset blokady FLIP-in — ponowne otwarcie ma znów animować (ref
      // przypnie się na renderze z open=true, PRZED tym efektem).
      flipInDoneRef.current = false;
      const content = contentRef.current;
      const overlay = overlayRef.current;
      flipAnimRef.current?.cancel();

      overlay?.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 200, easing: 'ease-in', fill: 'both' });

      // Final rect = rzeczywisty rect contentu (arkusz, nie viewport — plan 5.1).
      const toTransform =
        prefersReducedMotion() || !content
          ? ''
          : buildFlipTransform(originRef.current, content.getBoundingClientRect());
      if (!content || !toTransform) {
        const fade = content?.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 200, easing: 'ease-in', fill: 'both' });
        // Race C-1: odmontuj tylko, gdy dialog NIE został w międzyczasie
        // ponownie otwarty — openRef trzyma aktualne `open` (closure ma stare).
        const done = () => {
          if (!openRef.current) setRendered(false);
        };
        if (fade) {
          fade.onfinish = done;
          flipAnimRef.current = fade;
        } else {
          setRendered(false);
        }
        return;
      }
      content.style.transformOrigin = 'center center';
      // will-change: transform tylko na czas zwijania — zdejmowane w finish/cancel.
      content.style.willChange = 'transform';
      const anim = content.animate(
        [
          { transform: 'translate(0px, 0px) scale(1, 1)', opacity: 1 },
          { transform: toTransform, opacity: 0.4 },
        ],
        { ...FLIP_TIMING, duration: 280 }
      );
      const finish = () => {
        content.style.willChange = '';
        // Race C-1: gdy w trakcie ~280 ms zwijania dialog został ponownie
        // otwarty (open=true), finish/cancel NIE może zdjąć go z DOM.
        if (!openRef.current) setRendered(false);
      };
      anim.onfinish = finish;
      anim.oncancel = finish;
      // Animacja zwijania w refie: ponowne otwarcie (runFlipIn) anuluje ją
      // jawnie (oncancel → guard openRef), zamiast zostawiać równoległą
      // animację transformu z fill:both.
      flipAnimRef.current = anim;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, rendered]);

  // ---------------------------------------------------------------------------
  // Tryb edycji całości diagnozy (plan 4.2)
  // ---------------------------------------------------------------------------
  const [editMode, setEditMode] = React.useState(false);
  const [editView, setEditView] = React.useState<'edit' | 'preview'>('edit');
  const [draft, setDraft] = React.useState('');
  // Treść wyjściowa edycji — punkt odniesienia dla „czy są niezapisane zmiany".
  const baseDraftRef = React.useRef('');

  // Focus textarea TYLKO przy wejściu w tryb edycji (5.3d) — nie przy
  // przełączaniu zakładki Edycja/Podgląd (textarea jest stale zamontowana
  // w stacku, więc autoFocus-atrybut by tu nie zadziałał i nie ma go być).
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);
  const prevEditModeRef = React.useRef(false);
  React.useEffect(() => {
    if (!prevEditModeRef.current && editMode) {
      textareaRef.current?.focus();
    }
    prevEditModeRef.current = editMode;
  }, [editMode]);

  const canEdit = Boolean(interpretation) && !isStreaming && Boolean(onSaveEditedMarkdown);
  const isDirty = editMode && draft !== baseDraftRef.current;

  const enterEditMode = () => {
    if (!canEdit || !interpretation) return;
    // Źródło treści: pełna odpowiedź markdown; gdy jej brak (np. wczytana
    // zapisana diagnoza) — złożenie sekcji do markdownu o kanonicznych
    // nagłówkach, które parser na pewno rozpozna przy zapisie.
    const source = rawText.trim() !== '' ? rawText : buildMarkdownFromSections(interpretation);
    baseDraftRef.current = source;
    setDraft(source);
    setEditView('edit');
    setEditMode(true);
  };

  const exitEditMode = () => {
    setEditMode(false);
    setDraft('');
    baseDraftRef.current = '';
  };

  /** Anuluj (przycisk/Esc): przy zmianach wymaga potwierdzenia. Zwraca true, gdy edycja została porzucona. */
  const cancelEdit = (): boolean => {
    if (isDirty && !window.confirm(UNSAVED_CHANGES_CONFIRM)) return false;
    exitEditMode();
    return true;
  };

  const saveEdit = () => {
    if (!onSaveEditedMarkdown) return;
    onSaveEditedMarkdown(draft);
    // Po zapisie zostajemy w dialogu — sekcje pod spodem są już zaktualizowane.
    exitEditMode();
  };

  // Zamknięcie dialogu (X / overlay / Esc poza edycją): w trybie edycji
  // z niezapisanymi zmianami wymaga confirm; samo otwarcie/zamknięcie NIE
  // zmienia żadnego stanu strony pod spodem (overlay-only, plan 4.2).
  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen && editMode) {
      if (!cancelEdit()) return;
    }
    onOpenChange(nextOpen);
  };

  // Auto-scroll podglądu streamowania (5.3b — ta sama semantyka co w
  // useStreaming): scroll NATYCHMIASTOWY (bez behavior:'smooth' — kolejkowane
  // płynne scrolle co chunk walczą ze sobą) i TYLKO gdy użytkownik jest
  // przyklejony do dołu. Przyklejenie liczone względem scrollHeight sprzed
  // bieżącego appendu (ref), próg 40 px; ręczne odscrollowanie wyłącza follow
  // aż użytkownik wróci na dół.
  const dialogLastScrollHeightRef = React.useRef(0);
  React.useEffect(() => {
    const el = streamScrollRef.current;
    if (!open || !isStreaming || editMode || !el) {
      dialogLastScrollHeightRef.current = 0;
      return;
    }
    const prevScrollHeight = dialogLastScrollHeightRef.current;
    const pinnedToBottom =
      prevScrollHeight === 0 ||
      el.scrollTop + el.clientHeight >= prevScrollHeight - 40;
    if (pinnedToBottom) {
      el.scrollTop = el.scrollHeight;
    }
    dialogLastScrollHeightRef.current = el.scrollHeight;
  }, [open, isStreaming, streamingText, editMode]);

  // W4: krótki „ghost" streamu po jego końcu — blok streamu zostaje
  // zamontowany jeszcze 150 ms z opacity→0, a sekcje wchodzą kaskadą
  // w tym samym obszarze grid-stacka = crossfade stream→sekcje.
  const [streamGhost, setStreamGhost] = React.useState(false);
  const prevIsStreamingRef = React.useRef(isStreaming);
  React.useEffect(() => {
    const wasStreaming = prevIsStreamingRef.current;
    prevIsStreamingRef.current = isStreaming;
    if (isStreaming) {
      setStreamGhost(false);
      return;
    }
    if (wasStreaming && open) {
      setStreamGhost(true);
      const timer = window.setTimeout(() => setStreamGhost(false), 150);
      return () => window.clearTimeout(timer);
    }
  }, [isStreaming, open]);

  // Zamknięcie dialogu zamyka też menu eksportu i porzuca tryb edycji
  // (decyzja o niezapisanych zmianach zapadła już w handleOpenChange).
  React.useEffect(() => {
    if (!open) {
      setExportMenuOpen(false);
      setEditMode(false);
      setDraft('');
      baseDraftRef.current = '';
    }
  }, [open]);

  const hasMarkdown = rawText.trim() !== '';

  // Memoizacja renderowania markdownu STATYCZNYCH tekstów — by nie przeliczać
  // dużego HTML przy każdym re-renderze (np. otwarcie menu eksportu, zmiana
  // editView). streamingText celowo NIE jest memoizowany — i tak zmienia się co
  // chunk, memo dawałoby tylko narzut.
  const rawTextHtml = React.useMemo(() => DOMPurify.sanitize(renderMarkdownToHtml(rawText)), [rawText]);
  // Podgląd edycji: w stacku (5.3d) warstwa podglądu jest stale zamontowana
  // podczas edycji, więc HTML liczymy dla CAŁEGO editMode (nie tylko zakładki
  // „Podgląd") — przełączenie zakładek nie podmienia wtedy innerHTML i podgląd
  // trzyma scroll. Memo po treści draftu ucina przeliczanie na re-renderach.
  const draftPreviewHtml = React.useMemo(
    () => (editMode ? DOMPurify.sanitize(renderMarkdownToHtml(draft)) : ''),
    [editMode, draft]
  );
  // Markdown streamu: liczony TYLKO podczas streamingu (+ ghost crossfade'u
  // W4) i tylko przy nowym chunku — inline w JSX przeliczałby cały (rosnący!)
  // markdown przy KAŻDYM re-renderze dialogu, niezależnie od przyczyny (C-2).
  const streamingHtml = React.useMemo(
    () => (isStreaming || streamGhost ? DOMPurify.sanitize(renderMarkdownToHtml(streamingText)) : ''),
    [isStreaming, streamGhost, streamingText]
  );

  // M-3: kaskada wejścia sekcji (ai-section-*) odpala się tylko przy PIERWSZYM
  // pokazaniu treści po otwarciu dialogu. Wyjście z trybu edycji remountuje
  // InterpretationSections (render warunkowy) — bez tej blokady animacja
  // leciałaby od nowa po każdym „Anuluj"/„Zapisz".
  const sectionsAnimatedRef = React.useRef(false);
  const prevSectionsVisibleRef = React.useRef(false);
  // `open` w warunku jest KONIECZNE (5.3e): bez niego krawędź „sekcje znikają"
  // wykrywana była też przy ZAMKNIĘTYM dialogu (start streamu przy istniejącej
  // interpretacji → isStreaming=true), ref oznaczał „już animowano" i kaskada
  // W4 po otwarciu arkusza nigdy nie leciała.
  const sectionsVisible = open && !editMode && !isStreaming && Boolean(interpretation);
  React.useEffect(() => {
    // Oznacz „już animowano" dopiero gdy sekcje ZNIKAJĄ (wejście w edycję) —
    // w trakcie pierwszego pokazania prop animateIn pozostaje stabilnie true,
    // żeby re-render nie ucinał trwającej kaskady. Detekcja krawędzi przez
    // prev-ref (idempotentna — bezpieczna w StrictMode), nie cleanup efektu.
    if (prevSectionsVisibleRef.current && !sectionsVisible) {
      sectionsAnimatedRef.current = true;
    }
    prevSectionsVisibleRef.current = sectionsVisible;
  }, [sectionsVisible]);
  React.useEffect(() => {
    // Zamknięcie dialogu resetuje blokadę — następne otwarcie znów animuje.
    if (!open) sectionsAnimatedRef.current = false;
  }, [open]);

  const editedBadge = diagnosisEdited ? (
    <span className="text-[10px] font-medium text-txt-tertiary border border-brd rounded-full px-2 py-0.5 shrink-0">
      edytowano
    </span>
  ) : null;

  // Dopóki trwa animacja zwijania, dialog zostaje w DOM (rendered), choć
  // Radix open=false. Po zakończeniu FLIP-out odmontowujemy całość.
  if (!rendered) return null;

  return (
    <Dialog.Root open={open} onOpenChange={handleOpenChange}>
      <Dialog.Portal forceMount>
        {/* Overlay tła: WIZJA „SOLIDNY DOKUMENT" — wyraźne przyciemnienie BEZ
            backdrop-filter (blur na pełnym ekranie = główna przyczyna mulenia).
            Fade sterowany w JS (element.animate) — forceMount, bez CSS data-state. */}
        <Dialog.Overlay
          ref={overlayRef}
          forceMount
          className="fullscreen-result-overlay fixed inset-0 z-[9990]"
        />
        <Dialog.Content
          ref={setContentNode}
          forceMount
          onEscapeKeyDown={(e) => {
            // Esc w trybie edycji = Anuluj (z confirmem przy zmianach),
            // NIE zamyka dialogu — Radix nasłuchuje na document (capture),
            // więc blokujemy domyślne zamknięcie i obsługujemy lokalnie.
            if (editMode) {
              e.preventDefault();
              cancelEdit();
            }
          }}
          className="fullscreen-result-content fixed inset-0 mx-auto sm:inset-y-6 sm:inset-x-0 sm:w-[min(56rem,calc(100vw-2rem))] sm:rounded-2xl bg-surface-page text-txt-primary z-[9991] flex flex-col overflow-hidden border border-brd shadow-[var(--shadow-float)] focus:outline-none"
        >
          {/* Opis dla czytników ekranu (Radix ostrzega bez Description) */}
          <Dialog.Description className="sr-only">
            Pełnoekranowy podgląd i edycja wyniku diagnozy. Esc zamyka.
          </Dialog.Description>
          {/* Nagłówek — półprzezroczyste solid tło BEZ backdrop-filter (szeroki
              pasek z filtrem też kosztuje; tu tylko lekka przezroczystość). */}
          <div className="flex items-center justify-between gap-3 px-4 py-3 sm:px-5 sm:py-3.5 border-b border-brd fullscreen-result-header shrink-0 flex-wrap">
            <Dialog.Title className="text-base font-semibold text-txt-primary flex items-center gap-2 min-w-0">
              <FileText className="w-5 h-5 text-accent-strong shrink-0" />
              <span className="truncate">
                Wynik diagnozy klinicznej — {patientName.trim() || 'Pacjent'}
              </span>
              {editedBadge}
            </Dialog.Title>
            {/* flex-wrap + justify-end: na telefonie przyciski łamią się w wiersze i „Zamknij”
                zostaje na ekranie (wcześniej shrink-0 wypychał Eksportuj i X poza krawędź). */}
            <div className="flex flex-wrap items-center justify-end gap-2 min-w-0 ml-auto">
              <button
                onClick={enterEditMode}
                disabled={!canEdit || editMode}
                title="Edytuj całą diagnozę jako markdown"
                aria-label="Edytuj diagnozę"
                className={`px-3 py-1.5 border rounded-xl text-xs font-bold flex items-center gap-1.5 transition-colors duration-200 disabled:cursor-not-allowed ${
                  editMode
                    ? 'bg-accent-strong border-accent text-white dark:text-emerald-950'
                    : 'bg-surface-card border-brd text-txt-secondary hover:text-txt-primary hover:bg-surface-subtle disabled:opacity-40'
                }`}
              >
                <Pencil className="w-3.5 h-3.5" />
                Edytuj
              </button>
              <button
                onClick={onCopyAll}
                disabled={!interpretation || editMode}
                title="Kopiuj całość raportu jako czysty tekst"
                className="px-3 py-1.5 bg-accent-soft border border-accent-border text-accent-strong hover:bg-accent-border rounded-xl text-xs font-bold flex items-center gap-1.5 transition-colors duration-200 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Copy className="w-3.5 h-3.5" />
                Kopiuj całość
              </button>
              <button
                onClick={onCopyMarkdown}
                disabled={!hasMarkdown || editMode}
                title="Kopiuj pełną odpowiedź w formacie Markdown"
                className="px-3 py-1.5 bg-surface-card border border-brd text-txt-secondary hover:text-txt-primary hover:bg-surface-subtle rounded-xl text-xs font-bold flex items-center gap-1.5 transition-colors duration-200 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <FileCode className="w-3.5 h-3.5" />
                Kopiuj markdown
              </button>
              <div className="relative">
                <button
                  onClick={() => setExportMenuOpen((v) => !v)}
                  disabled={!interpretation || editMode}
                  title="Eksportuj raport do pliku"
                  className="px-3 py-1.5 bg-accent-strong hover:bg-accent text-white dark:text-emerald-950 rounded-xl text-xs font-bold flex items-center gap-1.5 transition-colors duration-200 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <Download className="w-3.5 h-3.5" />
                  Eksportuj
                  <ChevronDown className="w-3.5 h-3.5" />
                </button>
                {exportMenuOpen && (
                  /* Nieprzezroczyste tanie tło (bez backdrop-filter) — dropdown
                     wewnątrz overflow-hidden/fixed kontenera (I-1). */
                  <div className="absolute top-full right-0 mt-2 w-[min(16rem,calc(100vw-2rem))] bg-surface-card border border-brd shadow-[var(--shadow-float)] rounded-xl overflow-hidden z-50 animate-fadeIn">
                    {EXPORT_OPTIONS.map(({ format, label, icon }, idx) => (
                      <button
                        key={format}
                        onClick={() => {
                          setExportMenuOpen(false);
                          onDownloadReport(format);
                        }}
                        className={`w-full px-4 py-3 hover:bg-surface-subtle text-txt-secondary hover:text-txt-primary font-semibold text-xs flex items-center gap-2 transition-colors text-left ${
                          idx < EXPORT_OPTIONS.length - 1 ? 'border-b border-brd' : ''
                        }`}
                      >
                        {icon}
                        {label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <Dialog.Close asChild>
                <button
                  aria-label="Zamknij pełny ekran"
                  title="Zamknij (Esc)"
                  className="p-2 bg-surface-card border border-brd text-txt-secondary hover:text-txt-primary hover:bg-surface-subtle rounded-xl transition-colors duration-200"
                >
                  <X className="w-4 h-4" />
                </button>
              </Dialog.Close>
            </div>
          </div>

          {/* Pasek narzędzi edytora — nad stackiem, widoczny przez CAŁY tryb
              edycji (zakładki Edycja/Podgląd nie zmieniają wysokości stacku). */}
          {editMode && (
            <div className="shrink-0 max-w-3xl mx-auto w-full px-5 pt-4">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div
                  className="flex items-center bg-surface-subtle border border-brd rounded-xl p-0.5"
                  role="group"
                  aria-label="Tryb edytora"
                >
                  <button
                    onClick={() => setEditView('edit')}
                    aria-pressed={editView === 'edit'}
                    className={`px-3 py-1 rounded-[10px] text-xs font-bold transition-colors duration-200 ${
                      editView === 'edit'
                        ? 'bg-surface-card text-txt-primary shadow-sm border border-brd'
                        : 'text-txt-secondary hover:text-txt-primary'
                    }`}
                  >
                    Edycja
                  </button>
                  <button
                    onClick={() => setEditView('preview')}
                    aria-pressed={editView === 'preview'}
                    className={`px-3 py-1 rounded-[10px] text-xs font-bold transition-colors duration-200 ${
                      editView === 'preview'
                        ? 'bg-surface-card text-txt-primary shadow-sm border border-brd'
                        : 'text-txt-secondary hover:text-txt-primary'
                    }`}
                  >
                    Podgląd
                  </button>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] text-txt-tertiary tabular-nums" aria-live="polite">
                    {draft.length} znaków
                  </span>
                  <button
                    onClick={saveEdit}
                    className="px-3.5 py-1.5 bg-accent-strong hover:bg-accent text-white dark:text-emerald-950 rounded-xl text-xs font-bold transition-colors duration-200"
                  >
                    Zapisz
                  </button>
                  <button
                    onClick={() => cancelEdit()}
                    title="Anuluj (Esc)"
                    className="px-3.5 py-1.5 bg-surface-card hover:bg-surface-subtle text-txt-secondary border border-brd rounded-xl text-xs font-bold transition-colors duration-200"
                  >
                    Anuluj
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ---------------------------------------------------------------
             STACK (5.3d): widok / edytor / podgląd renderowane RÓWNOLEGLE
             w CSS grid-stacku (wspólny obszar col-start-1 row-start-1),
             przełączane opacity (120 ms var(--ease-out), .stack-layer)
             + pointer-events-none + aria-hidden na nieaktywnym — ZERO
             remountów: textarea trzyma scroll/zaznaczenie/treść, podgląd
             trzyma scroll, sekcje nie remountują się przy wyjściu z edycji.
             Wysokość stabilna: grid-rows-1 = minmax(0,1fr) wypełnia flex-1
             min-h-0; każda warstwa scrolluje własnym overflow-y-auto.
             Koszt równoległego montażu OK: InterpretationSections jest
             React.memo z propsem interpretation stabilnym podczas edycji.
             --------------------------------------------------------------- */}
          <div className="flex-1 min-h-0 grid grid-cols-1 grid-rows-1">
            {/* WARSTWA WIDOKU: pasek błędu W6 + stream ↔ sekcje (crossfade W4) */}
            <div
              ref={streamScrollRef}
              aria-hidden={editMode}
              className={`col-start-1 row-start-1 min-h-0 overflow-y-auto stack-layer ${
                editMode ? 'stack-layer-hidden' : ''
              }`}
            >
              <div className="max-w-3xl mx-auto px-5 py-6">
                {/* W6: błąd W TRAKCIE streamu — pasek błędu (tokeny --error)
                    nad częściową treścią; treść zostaje (salvage w hooku). */}
                {generationStatus === 'error' && !isStreaming && (
                  <div
                    role="alert"
                    className="stream-error-bar px-4 py-3 rounded-xl flex items-center justify-between gap-3 flex-wrap mb-5"
                  >
                    <div className="flex items-center gap-2 min-w-0 text-xs font-semibold">
                      <AlertTriangle className="w-4 h-4 shrink-0" />
                      <span>Generowanie przerwane błędem — wyświetlona treść może być częściowa.</span>
                    </div>
                    {onRetry && (
                      <button
                        onClick={onRetry}
                        className="px-3 py-1.5 bg-surface-card border border-brd text-txt-secondary hover:text-txt-primary hover:bg-surface-subtle rounded-lg text-xs font-bold flex items-center gap-1.5 transition-colors duration-200 shrink-0"
                      >
                        <RotateCcw className="w-3.5 h-3.5" />
                        Ponów
                      </button>
                    )}
                  </div>
                )}

                {/* Wewnętrzny grid-stack stream↔sekcje: przez 150 ms ghosta oba
                    bloki dzielą obszar — stream gaśnie (.stream-crossfade-out),
                    sekcje wchodzą kaskadą ai-section-* = crossfade W4. */}
                <div className="grid">
                  {(isStreaming || streamGhost) && (
                    <div
                      className={`col-start-1 row-start-1 min-w-0 flex flex-col gap-4 stream-crossfade ${
                        isStreaming ? '' : 'stream-crossfade-out'
                      }`}
                    >
                      <div className="flex items-center gap-2 text-sm font-bold text-txt-primary">
                        <Loader2 className="w-5 h-5 text-accent animate-spin" />
                        Generowanie Raportu Diagnostycznego...
                      </div>
                      <div className="p-5 bg-surface-card border border-accent-border rounded-xl text-sm leading-relaxed text-txt-primary shadow-inner select-text">
                        {/* Kursor jako OSOBNY sibling (5.3a): __html = czysta
                            treść — przeglądarka diffuje rosnący tekst zamiast
                            odtwarzać cały DOM przez doklejony kursor. */}
                        <div
                          className="prose dark:prose-invert"
                          dangerouslySetInnerHTML={{ __html: streamingHtml }}
                        />
                        <span aria-hidden="true" className="inline-block w-1.5 h-3 bg-accent ml-1 animate-pulse" />
                      </div>
                    </div>
                  )}
                  {!isStreaming && (
                    <div className="col-start-1 row-start-1 min-w-0">
                      {interpretation ? (
                        <InterpretationSections
                          interpretation={interpretation}
                          onCopySection={onCopySection}
                          size="reading"
                          animateIn={!sectionsAnimatedRef.current}
                        />
                      ) : hasMarkdown ? (
                        <div
                          className="p-5 bg-surface-card border border-brd rounded-xl text-sm leading-relaxed prose dark:prose-invert select-text"
                          dangerouslySetInnerHTML={{ __html: rawTextHtml }}
                        />
                      ) : (
                        <p className="text-sm text-txt-tertiary text-center py-12">
                          Brak wyniku do wyświetlenia — wygeneruj interpretację.
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* WARSTWA EDYTORA (textarea) — zamontowana równolegle, gdy edycja
                jest dostępna (dialog otwarty + interpretacja). Bez autoFocus:
                focus nadaje efekt WYŁĄCZNIE na wejściu w editMode. */}
            {canEdit && (
              <div
                aria-hidden={!(editMode && editView === 'edit')}
                className={`col-start-1 row-start-1 min-h-0 stack-layer flex flex-col ${
                  editMode && editView === 'edit' ? '' : 'stack-layer-hidden'
                }`}
              >
                <div className="max-w-3xl mx-auto w-full px-5 py-4 flex flex-col flex-1 min-h-0">
                  <textarea
                    ref={textareaRef}
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    aria-label="Edytor pełnej diagnozy (markdown)"
                    spellCheck={false}
                    tabIndex={editMode && editView === 'edit' ? 0 : -1}
                    className="flex-1 min-h-0 w-full font-sans text-sm md:text-base leading-relaxed bg-surface-card border border-brd rounded-xl p-5 text-txt-primary resize-none focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                  />
                </div>
              </div>
            )}

            {/* WARSTWA PODGLĄDU DRAFTU — równolegle z textarea: przełączanie
                zakładek nie remountuje niczego, kontener trzyma scroll. */}
            {canEdit && (
              <div
                aria-hidden={!(editMode && editView === 'preview')}
                className={`col-start-1 row-start-1 min-h-0 stack-layer flex flex-col ${
                  editMode && editView === 'preview' ? '' : 'stack-layer-hidden'
                }`}
              >
                <div className="max-w-3xl mx-auto w-full px-5 py-4 flex flex-col flex-1 min-h-0">
                  <div
                    className="flex-1 min-h-0 overflow-y-auto p-5 bg-surface-card border border-brd rounded-xl text-sm leading-relaxed prose dark:prose-invert select-text"
                    dangerouslySetInnerHTML={{ __html: draftPreviewHtml }}
                  />
                </div>
              </div>
            )}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
